import { Parent } from './contact.model';
import { PaymentInfo, PaymentInfoWithReferences } from './payment.model';
import { Registration } from './registration.model';
import { Contract } from './contract.model';
import { SchoolYear } from './schoolyear.model';
import { Lunch, Module, School } from './schools.model';
import type { Weekday } from './lunch.model';
import { LanguageCode } from './language.model';
import type { ClassType } from './grade.model';
import type { VacationPeriodConfiguration } from './holiday-care-registration.model';
import { DocumentReference, Timestamp } from 'firebase/firestore';

// WARNING: This model is also used in Firebase Functions (functions/src/_shared/student.model.ts)
// Keep core data structures and business logic functions synchronized between frontend and backend!
// UI-specific code (status configs, labels) is in student-status.model.ts
export interface Student {
  id: string;
  userId: string;
  userRef: DocumentReference;
  createdOn: Timestamp;
  firstName: string;
  lastName: string;
  firstNameLower: string;
  lastNameLower: string;
  isActive: boolean;
  /**
   * Soft-Delete-Metadaten (#966). Gesetzt vom Mitarbeiter-Portal beim
   * Löschen bzw. Wiederherstellen eines Schülers — ohne sie ist im
   * Nachhinein nicht nachvollziehbar, wer einen Datensatz aus den Listen
   * genommen hat (`lastModified*` trägt dann noch den letzten fachlichen
   * Schreibvorgang, im Zweifel den Eltern-Account). Metadaten, kein
   * Stammdatum: in SKIP_FIELDS des Change-Detectors.
   */
  deletedOn?: Timestamp | null;
  deletedBy?: string | null;
  deletedByEmail?: string | null;
  restoredOn?: Timestamp | null;
  restoredBy?: string | null;
  restoredByEmail?: string | null;
  holidayCare?: boolean;
  /**
   * Doubletten-Marker (#710) — Systemmetadaten, gepflegt vom Trigger
   * `syncStudentDuplicateMarkers`. NICHT exportrelevant, in SKIP_FIELDS.
   */
  duplicateSuspect?: boolean;
  /** Gruppierungs-Schlüssel: firstNameLower|lastNameLower|birthDateIsoDay */
  duplicateClusterKey?: string;
  /** Cluster existiert nur schuljahresübergreifend (evtl. legitime Folgeanmeldung) */
  duplicateCrossYearOnly?: boolean;
  /** Anmeldung wurde trotz Doubletten-Warnung bestätigt — wird erst mit #751 (Prävention) befüllt */
  duplicateConfirmedByUser?: boolean;
  /**
   * Erledigt-Zustand #710 — von Mitarbeitern gesetzt, Trigger räumt bei
   * Cluster-Änderung. Invariante (User-Feedback 2026-07-10): JEDER Schüler
   * mit `duplicateSuspect: true` trägt IMMER ein explizites `boolean` (nie
   * `undefined`) — server-seitige Filterbarkeit via
   * `where('duplicateSuspect','==',true).where('duplicateDismissed','==',
   * false)`. Das Feld wird nur zusammen mit den 3 Metadaten-Feldern gelöscht,
   * wenn der Marker selbst entfällt (Cluster löst sich auf / Mitglied
   * verlässt den Cluster).
   */
  duplicateDismissed?: boolean;
  duplicateDismissedBy?: string;
  duplicateDismissedByEmail?: string;
  duplicateDismissedOn?: Timestamp;
  birthDate: Timestamp;
  gender: Gender;
  schoolId: string;
  schoolRef: DocumentReference;
  startingGrade: number;
  currentGrade: number;
  /**
   * Klassenform. 'regular' = normale Klasse (inkl. Vorklasse via grade=0),
   * 'paktklasse' = 4-jähriger Klassenverbund (siehe grade.model.ts).
   * Optional für Abwärtskompatibilität mit Bestandsdaten (undefined === 'regular').
   */
  classType?: ClassType;
  currentSchoolYear: number;
  updatedOn?: Timestamp;
  language: LanguageCode;
  currentStatus: StudentStatus;

  //Registration
  healthInfo: HealthInfo;
  parentInfo: ParentInfo;
  yearInfo: SchoolYear;
  dataPrivacy: {
    hasAccepted: boolean;
    acceptedOn: Timestamp;
    version: string;
  };
  startInfo: {
    school: Partial<School>;
    module: Module;
    /** @deprecated alte Lunch-Auswahl, weiterhin geschrieben für Kompatibilität */
    lunch?: Lunch;
    /** @deprecated alte Lunch-Auswahl */
    includeLunch: boolean;
    /** Neu ab 2026-05 — ID der gewählten LunchConfiguration aus school.lunchConfigurations */
    lunchConfigurationId?: string;
    /** Nur bei DAILY_SELECTION */
    selectedLunchDays?: Weekday[];
    /**
     * Reine Bedarfsabfrage (#915): Wird Betreuung vor Unterrichtsbeginn benoetigt?
     * Keine Buchung und keine Preisfolge — dient der Planung der Einrichtungen.
     */
    needsCareBeforeSchool?: boolean;
  };
  //Contract
  miscInfo: MiscInfo;
  paymentInfo: PaymentInfo | PaymentInfoWithReferences;

  lastEmailStatus?: {
    success: boolean;
    type: string;
    timestamp: Timestamp;
    emailId?: string;
  };

  registration: Registration;
  contract?: Contract;

  /**
   * Genehmigte, aber noch nicht wirksame Vertragsänderung (#677).
   * Gesetzt vom Alteration-Trigger beim Approve mit Zukunftsdatum,
   * gelöscht bei applied/cancelled/rejected. Rein informativ für die UIs.
   */
  scheduledContractChange?: {
    alterationId: string;
    effectiveDate: Timestamp;
    /** Optional, damit Alt-Marker (vor Backfill) gültig bleiben. */
    summary?: ScheduledChangeSummary;
  };

  /**
   * Stichtag, ab dem die aktuell hinterlegten Betreuungsdaten (Modul, Mittagessen)
   * gelten (#866). Gesetzt vom Mitarbeiter-Portal bei einer sofortigen oder
   * rückwirkenden Änderung und von der Alteration-Pipeline beim Wirksamwerden
   * einer terminierten Änderung. Fehlt bei Schülern, deren Betreuungsdaten seit
   * Einführung des Feldes nicht geändert wurden.
   */
  careChangeEffectiveDate?: Timestamp;
}

/**
 * Vorberechnete Anzeige-Zusammenfassung einer geplanten Vertragsänderung.
 * Geschrieben von der Genehmigungs-Function, damit die UIs den Inhalt der
 * Änderung ohne Alteration-/School-Read anzeigen können.
 */
export interface ScheduledChangeSummary {
  moduleFrom: string;
  moduleTo: string;
  scheduleFrom: string;
  scheduleTo: string;
  /** Aufgelöstes Mittagessen-Label, null = kein Mittagessen. */
  lunchFrom: string | null;
  lunchTo: string | null;
  /** Modul- + Mittagessen-Gebühr pro Monat. */
  totalFrom: number;
  totalTo: number;
  currency: string;
}

export interface HealthInfo {
  allergies: boolean;
  allergiesDescription: string;
  chronicDiseases: boolean;
  chronicDiseasesDescription: string;
  measlesVaccination: boolean;
  measlesVaccinationConsent?: boolean;
  tetanusVaccinationDate?: Timestamp | null;
  /** Impfung nicht möglich (Kontraindikation) — Datum entfällt dann. */
  tetanusVaccinationNotPossible?: boolean;
  /** Ärztliche Bescheinigung der Kontraindikation liegt vor. */
  tetanusVaccinationDoctorCertificate?: boolean;
  nutrition: NutritionType;
  nutritionOther?: string;
  /**
   * Nur relevant wenn `allergies === true` (#914): Bekommt das Kind
   * Notfallmedikamente? Loest im Portal den Hinweis auf die beiden
   * vorzulegenden Formulare aus (UKH-Notfallplan, DAAB-Ermaechtigung).
   */
  emergencyMedication?: boolean;
}

export type NutritionType =
  | 'normal'
  | 'no_pork'
  | 'vegetarian'
  | 'vegan'
  | 'other';

export type Gender = 'male' | 'female' | 'diverse';

export interface ParentInfo {
  parent1: Parent;
  parent2?: Parent;
  singleParenting: boolean;
  hasSibling: boolean;
  siblingName?: string;
  siblingUseCurrentSchool?: 'same' | 'other-awo' | 'other';
  siblingSchoolName?: string;
}

export interface MiscInfo {
  emergencyName: string;
  emergencyPhone: string;
  healthInsuranceName: string;
  healthInsuranceWith: string;
  /**
   * Interner Marker: Bei "Sonstige" (z. B. Großeltern) enthält dieses Feld den
   * frei eingegebenen Namen. `healthInsuranceWith` hält denselben Klarnamen für
   * Anzeige/Export; `healthInsuranceWithOther` dient nur zur eindeutigen
   * Erkennung der "Sonstige"-Auswahl beim erneuten Laden. Nicht im Export.
   */
  healthInsuranceWithOther?: string;
  afterSchoolArrangement?: AfterSchoolOption; // DEACTIVATED: Made optional
  afterSchoolArrangementDetails?: string;
  photoConsentOptOut?: boolean; // Widerspruch Bildfreigabe
}

/** Sentinel-Wert des "Mitversichert bei"-Dropdowns für freie Eingabe. */
export const HEALTH_INSURANCE_OTHER = 'other';

/**
 * Löst die Form-Werte des "Mitversichert bei"-Dropdowns in die zu speichernden
 * Werte auf: Bei "Sonstige" wird der Freitext zum Klarnamen in
 * `healthInsuranceWith`; andernfalls bleibt der Elternname erhalten und der
 * Marker wird geleert.
 */
export function resolveHealthInsuranceWith(value: {
  healthInsuranceWith?: string | null;
  healthInsuranceWithOther?: string | null;
}): { healthInsuranceWith: string; healthInsuranceWithOther: string } {
  if (value.healthInsuranceWith === HEALTH_INSURANCE_OTHER) {
    const other = (value.healthInsuranceWithOther ?? '').trim();
    return { healthInsuranceWith: other, healthInsuranceWithOther: other };
  }
  return {
    healthInsuranceWith: value.healthInsuranceWith ?? '',
    healthInsuranceWithOther: '',
  };
}

type AfterSchoolOption =
  | 'picked_up'
  | 'independent_departure'
  | 'bus_transport';

// Comprehensive student status that merges registration and contract states
export type StudentStatus =
  // No registration exists
  | 'no_registration'
  // Registration states
  | 'registration_new'
  | 'registration_custom_start_date_pending'
  | 'registration_waitlist'
  | 'registration_contract_finalization'
  | 'registration_completed'
  | 'registration_cancelled'
  | 'registration_deleted'
  | 'registration_cancellation_requested'
  // Contract states (when registration is completed)
  | 'contract_active'
  | 'contract_renewal_pending'
  | 'contract_terminated'
  | 'contract_cancelled'
  | 'contract_cancellation_requested'
  | 'contract_cancellation_approved';

/**
 * Calculate contract end date based on start date and duration
 * Contract ends on July 31st + X years from the start year
 */
export function calculateContractEndDate(
  startDate: Date,
  durationYears: number
): Date {
  const startYear = startDate.getFullYear();
  // Contract ends on July 31st + durationYears from start year
  return new Date(startYear + durationYears, 6, 31); // July is month 6 (0-indexed)
}

/**
 * Check if student can register for holiday care based on contract validity period
 */
export function canStudentRegisterForVacation(
  student: Student,
  vacationStartDate: Date,
  currentDate: Date = new Date()
): { canRegister: boolean; reason?: string } {
  // Must have a contract to register for holiday care
  if (!student.contract) {
    return {
      canRegister: false,
      reason: 'Kein gültiger Vertrag vorhanden',
    };
  }

  const contractStart = student.contract.validFrom.toDate();

  // Calculate contract end date if not already set
  let contractEnd: Date;
  if (student.contract.validUntil) {
    contractEnd = student.contract.validUntil.toDate();
  } else {
    // Fallback calculation - contract runs until July 31st + 1 year
    contractEnd = calculateContractEndDate(contractStart, 1);
  }

  // Check if vacation period falls within contract validity
  if (vacationStartDate < contractStart) {
    return {
      canRegister: false,
      reason: 'Vertrag ist noch nicht gültig für diesen Zeitraum',
    };
  }

  if (vacationStartDate > contractEnd) {
    return {
      canRegister: false,
      reason: 'Vertrag ist abgelaufen für diesen Zeitraum',
    };
  }

  return { canRegister: true };
}

/**
 * Vertragsprüfung für eine ganze Ferienzeit (#1101).
 *
 * Eine Ferienzeit zählt zu dem Schuljahr, in dem sie beginnt: Die Sommerferien
 * gehören komplett zum alten Schuljahr — auch die Woche im August, obwohl
 * Verträge am 31.07. enden. Herbstferien gehören zum neuen Schuljahr und
 * bleiben für einen auslaufenden Vertrag gesperrt (#183). Geprüft wird deshalb
 * der Beginn der Ferienzeit, nie eine einzelne Woche.
 */
export function canStudentRegisterForVacationPeriod(
  student: Student,
  vacationPeriod: Pick<VacationPeriodConfiguration, 'dateRange'>,
  currentDate: Date = new Date()
): { canRegister: boolean; reason?: string } {
  return canStudentRegisterForVacation(
    student,
    vacationPeriod.dateRange.start.toDate(),
    currentDate
  );
}

//ToDo: To Be deleted?
export interface UserData {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  createdOn: Timestamp;
  isActive: boolean;
  role: string;
  provider: string;
  lastUpdated: Timestamp;
}
